// src/components/shopping-cart.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

const TOURS = [
  {
    id: "city-walk",
    name: "Downtown City Walk",
    price: 20,
    duration: "3–4 hrs",
    icon: "🚶",
    tag: "Most Popular",
    desc: "Walk the heartbeat of Nairobi — markets, history, and local life.",
    path: "/city-walk",
  },
  {
    id: "historical-cultural",
    name: "Historical & Cultural Tour",
    price: 80,
    duration: "3-4 hrs",
    icon: "🏛️",
    tag: "Cultural",
    desc: "National Museum, Karen Blixen, Bomas of Kenya.",
    path: "/historical-cultural",
  },
  {
    id: "slum-tours",
    name: "Kibera Slum Tour",
    price: 30,
    duration: "3 hrs",
    icon: "🤝",
    tag: "Community",
    desc: "An authentic, inspiring community experience in Kibera.",
    path: "/slum-tours",
  },
  {
    id: "naivasha",
    name: "Naivasha Excursion",
    price: 120,
    duration: "Full Day",
    icon: "🌊",
    tag: "Adventure",
    desc: "Lake Naivasha boat rides, Crescent Island & Hell's Gate.",
    path: "/excursions",
    location: "Naivasha",
  },
  {
    id: "wildlife",
    name: "Nairobi Wildlife Excursion",
    price: 100,
    duration: "4-5 hrs",
    icon: "🦒",
    tag: "Wildlife",
    desc: "Giraffe Centre, elephant orphanage & Nairobi Safari Walk.",
    path: "/excursions",
  },
];

const WHATSAPP_NUMBER = "254105057005";

function buildWhatsAppMessage({ firstName, tourName, tourDate, tourTime, guests, total }) {
  const msg = `Hello, I'd like to book a tour.

*Name:* ${firstName}
*Tour:* ${tourName}
*Date:* ${tourDate}
*Time:* ${tourTime}
*Guests:* ${guests}
*Total:* $${total} USD

Payment will be made on-site. Please confirm my booking. Thank you.`;
  return encodeURIComponent(msg);
}

export default function ShoppingCart() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [step, setStep] = useState(1); // 1 = select tour, 2 = details, 3 = confirm
  const [selectedTour, setSelectedTour] = useState(null);
  const [guests, setGuests] = useState(1);
  const [tourDate, setTourDate] = useState("");
  const [tourTime, setTourTime] = useState("10:00");
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios
      .get(`/session`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => navigate("/login"));
  }, [navigate]);

  const total = selectedTour ? selectedTour.price * guests : 0;

  const validateStep2 = () => {
    const e = {};
    if (!tourDate) e.tourDate = "Please select a tour date.";
    else {
      const d = new Date(tourDate);
      const today = new Date(); today.setHours(0,0,0,0);
      if (d < today) e.tourDate = "Date must be today or in the future.";
    }
    if (!tourTime) e.tourTime = "Please select a time.";
    if (guests < 1 || guests > 50) e.guests = "Guests must be between 1 and 50.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleBook = async () => {
    // Save booking to backend (no payment required — on-site)
    try {
      await axios.post(
        `/payment`,
        {
          totalAmount: total,
          paymentOption: "On-Site",
          tourDate,
          tourTime,
          howManyAreComing: guests,
          transactionId: null,
          tourName: selectedTour.name,
        },
        { withCredentials: true }
      );
    } catch (_) {
      // Non-blocking — WhatsApp still opens
    }

    // Open WhatsApp
    const msg = buildWhatsAppMessage({
      firstName: user?.firstName || "Guest",
      tourName: selectedTour.name,
      tourDate,
      tourTime,
      guests,
      total,
    });
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`, "_blank");
    setSubmitted(true);
  };

  const today = new Date().toISOString().split("T")[0];

  /* ── SUCCESS SCREEN ── */
  if (submitted) {
    return (
      <div style={styles.root}>
        <div style={styles.successWrap}>
          <div style={styles.successIcon}>✓</div>
          <h2 style={styles.successTitle}>Booking Sent!</h2>
          <p style={styles.successText}>
            Your WhatsApp message has been opened. Our team will confirm your booking shortly.
            Payment is collected on the day of your tour.
          </p>
          <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
            <button style={styles.btnGold} onClick={() => navigate("/")}>Back to Home</button>
            <button style={styles.btnOutline} onClick={() => { setSubmitted(false); setStep(1); setSelectedTour(null); setGuests(1); }}>
              Book Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&display=swap');
        :root { --gold:#C9A84C; --gold-light:#E8C97A; --dark:#0A0A0A; --charcoal:#141414; }
        .sc-root { font-family:'Jost',sans-serif; background:var(--dark); color:#fff; min-height:100vh; }
        .sc-hero {
          background: linear-gradient(160deg,#0d0b06 0%,#1a1500 55%,#0A0A0A 100%);
          border-bottom:1px solid rgba(201,168,76,0.2);
          padding:5rem 1.5rem 3.5rem;
          text-align:center;
          position:relative; overflow:hidden;
        }
        .sc-hero::before {
          content:''; position:absolute; inset:0;
          background:radial-gradient(ellipse 70% 60% at 50% 100%,rgba(201,168,76,0.07) 0%,transparent 70%);
          pointer-events:none;
        }
        .sc-eyebrow { font-size:.68rem; letter-spacing:5px; text-transform:uppercase; color:var(--gold); margin-bottom:.8rem; }
        .sc-title { font-family:'Cormorant Garamond',serif; font-size:clamp(2.4rem,6vw,4rem); font-weight:700; color:#fff; margin-bottom:.4rem; }
        .sc-title em { font-style:italic; color:var(--gold-light); }
        .sc-divider { width:50px; height:2px; background:var(--gold); margin:1.2rem auto 1.5rem; }
        .sc-subtitle { font-size:.9rem; font-weight:300; color:rgba(255,255,255,0.5); max-width:520px; margin:0 auto; line-height:1.8; }

        /* Steps */
        .sc-steps { display:flex; justify-content:center; gap:0; margin:2.5rem auto 0; max-width:400px; }
        .sc-step { flex:1; text-align:center; position:relative; }
        .sc-step-dot {
          width:32px; height:32px; border-radius:50%; background:rgba(255,255,255,0.08);
          border:1px solid rgba(201,168,76,0.25); display:flex; align-items:center;
          justify-content:center; font-size:.72rem; font-weight:600;
          color:rgba(255,255,255,0.35); margin:0 auto .5rem; transition:all .3s;
        }
        .sc-step.active .sc-step-dot { background:var(--gold); border-color:var(--gold); color:#0A0A0A; }
        .sc-step.done .sc-step-dot { background:rgba(201,168,76,0.2); border-color:var(--gold); color:var(--gold); }
        .sc-step-label { font-size:.6rem; letter-spacing:2px; text-transform:uppercase; color:rgba(255,255,255,0.3); }
        .sc-step.active .sc-step-label, .sc-step.done .sc-step-label { color:var(--gold); }
        .sc-step-line {
          position:absolute; top:15px; left:calc(50% + 16px); right:calc(-50% + 16px);
          height:1px; background:rgba(201,168,76,0.2);
        }
        .sc-step.done .sc-step-line { background:var(--gold); }

        /* Body */
        .sc-body { max-width:960px; margin:0 auto; padding:4rem 1.5rem 6rem; }

        /* Tour cards */
        .tour-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(260px,1fr)); gap:1.2rem; }
        .tour-select-card {
          background:rgba(255,255,255,0.03); border:1px solid rgba(201,168,76,0.14);
          border-radius:10px; padding:1.6rem 1.4rem; cursor:pointer;
          transition:border-color .3s, transform .3s, background .3s; text-align:left;
          position:relative; overflow:hidden;
        }
        .tour-select-card:hover { border-color:rgba(201,168,76,0.45); background:rgba(201,168,76,0.04); transform:translateY(-3px); }
        .tour-select-card.selected { border-color:var(--gold); background:rgba(201,168,76,0.08); }
        .tour-select-card.selected::after {
          content:'✓'; position:absolute; top:12px; right:14px; color:var(--gold);
          font-size:.9rem; font-weight:700;
        }
        .tsc-icon { font-size:2rem; margin-bottom:.8rem; display:block; }
        .tsc-tag { font-size:.6rem; letter-spacing:2px; text-transform:uppercase; color:var(--gold); margin-bottom:.4rem; }
        .tsc-name { font-family:'Cormorant Garamond',serif; font-size:1.25rem; font-weight:700; color:#fff; margin-bottom:.4rem; }
        .tsc-desc { font-size:.8rem; color:rgba(255,255,255,0.45); line-height:1.6; margin-bottom:.8rem; }
        .tsc-meta { display:flex; gap:1rem; font-size:.68rem; color:rgba(255,255,255,0.3); }
        .tsc-price { font-family:'Cormorant Garamond',serif; font-size:1.5rem; font-weight:700; color:var(--gold-light); margin-top:.6rem; }

        /* Form */
        .sc-form-card {
          background:rgba(255,255,255,0.03); border:1px solid rgba(201,168,76,0.18);
          border-radius:12px; padding:2.5rem 2rem;
        }
        .sc-form-group { margin-bottom:1.6rem; }
        .sc-label { font-size:.65rem; letter-spacing:3px; text-transform:uppercase; color:var(--gold); display:block; margin-bottom:.6rem; }
        .sc-input {
          width:100%; background:rgba(255,255,255,0.05); border:1px solid rgba(201,168,76,0.2);
          border-radius:6px; padding:.75rem 1rem; color:#fff; font-family:'Jost',sans-serif;
          font-size:.9rem; outline:none; transition:border-color .25s;
          appearance:none; -webkit-appearance:none; -moz-appearance:none;
        }
        .sc-input option {
          background:#0A0A0A; color:#fff;
        }
        .sc-input:focus { border-color:var(--gold); }
        .sc-input-error { border-color:rgba(220,80,80,0.7) !important; }
        .sc-error { font-size:.75rem; color:#e07070; margin-top:.4rem; }

        /* Guest counter */
        .guest-counter { display:flex; align-items:center; gap:1rem; }
        .gc-btn {
          width:40px; height:40px; border-radius:50%; background:rgba(201,168,76,0.1);
          border:1px solid rgba(201,168,76,0.3); color:var(--gold); font-size:1.2rem;
          cursor:pointer; display:flex; align-items:center; justify-content:center;
          transition:background .2s, border-color .2s;
        }
        .gc-btn:hover { background:rgba(201,168,76,0.2); border-color:var(--gold); }
        .gc-num { font-family:'Cormorant Garamond',serif; font-size:2rem; font-weight:700; color:#fff; min-width:2rem; text-align:center; }

        /* Summary card */
        .summary-card {
          background:rgba(201,168,76,0.06); border:1px solid rgba(201,168,76,0.25);
          border-radius:12px; padding:2rem;
        }
        .summary-row { display:flex; justify-content:space-between; align-items:center; margin-bottom:.9rem; font-size:.88rem; color:rgba(255,255,255,0.6); }
        .summary-row:last-child { margin-bottom:0; }
        .summary-row strong { color:#fff; }
        .summary-total { border-top:1px solid rgba(201,168,76,0.2); padding-top:1rem; margin-top:1rem; }
        .summary-total .summary-row { font-size:1rem; }
        .summary-total .summary-row strong { font-family:'Cormorant Garamond',serif; font-size:1.6rem; color:var(--gold-light); }

        /* WhatsApp note */
        .wa-note {
          background:rgba(37,211,102,0.08); border:1px solid rgba(37,211,102,0.25);
          border-radius:8px; padding:1.2rem 1.4rem; display:flex; gap:.8rem; align-items:flex-start;
          margin-bottom:1.5rem;
        }
        .wa-note-icon { font-size:1.3rem; flex-shrink:0; }
        .wa-note p { font-size:.83rem; color:rgba(255,255,255,0.65); line-height:1.65; margin:0; }
        .wa-note strong { color:#25D366; }

        /* Buttons */
        .btn-gold { background:var(--gold); color:#0A0A0A; border:none; padding:.85rem 2.4rem; border-radius:4px; font-family:'Jost',sans-serif; font-weight:600; font-size:.82rem; letter-spacing:2.5px; text-transform:uppercase; cursor:pointer; transition:background .25s,transform .2s; }
        .btn-gold:hover { background:var(--gold-light); transform:translateY(-2px); }
        .btn-gold:disabled { opacity:.5; cursor:not-allowed; transform:none; }
        .btn-outline { background:transparent; color:var(--gold); border:1px solid var(--gold); padding:.85rem 2.4rem; border-radius:4px; font-family:'Jost',sans-serif; font-weight:600; font-size:.82rem; letter-spacing:2px; text-transform:uppercase; cursor:pointer; transition:background .25s,color .25s; }
        .btn-outline:hover { background:rgba(201,168,76,0.1); }
        .btn-whatsapp { background:#25D366; color:#fff; border:none; padding:.85rem 2.4rem; border-radius:4px; font-family:'Jost',sans-serif; font-weight:600; font-size:.85rem; letter-spacing:1.5px; cursor:pointer; display:inline-flex; align-items:center; gap:.6rem; transition:background .25s,transform .2s; }
        .btn-whatsapp:hover { background:#20b356; transform:translateY(-2px); }

        .btn-row { display:flex; gap:1rem; flex-wrap:wrap; margin-top:2rem; }

        @media(max-width:640px) {
          .sc-form-card { padding:1.5rem 1.2rem; }
          .tour-grid { grid-template-columns:1fr; }
        }
      `}</style>

      <div className="sc-root">

        {/* HERO */}
        <div className="sc-hero">
          <p className="sc-eyebrow">Nairobi's Ultimate Experience</p>
          <h1 className="sc-title">Book Your <em>Adventure</em></h1>
          <div className="sc-divider" />
          <p className="sc-subtitle">
            Select your tour, choose your date, and confirm via WhatsApp. Payment is collected at the start of the tour.
          </p>

          {/* Steps indicator */}
          <div className="sc-steps">
            {["Choose Tour", "Details", "Confirm"].map((label, i) => {
              const num = i + 1;
              const cls = step === num ? "active" : step > num ? "done" : "";
              return (
                <div key={i} className={`sc-step ${cls}`}>
                  <div className="sc-step-dot">{step > num ? "✓" : num}</div>
                  <div className="sc-step-label">{label}</div>
                  {i < 2 && <div className="sc-step-line" />}
                </div>
              );
            })}
          </div>
        </div>

        <div className="sc-body">

          {/* ── STEP 1: SELECT TOUR ── */}
          {step === 1 && (
            <div>
              <p style={{ fontSize: ".65rem", letterSpacing: "5px", textTransform: "uppercase", color: "var(--gold)", marginBottom: ".8rem" }}>
                Step 1 of 3
              </p>
              <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 700, color: "#fff", marginBottom: "2rem" }}>
                Which tour calls to you?
              </h2>

              <div className="tour-grid">
                {TOURS.map((t) => (
                  <div
                    key={t.id}
                    className={`tour-select-card ${selectedTour?.id === t.id ? "selected" : ""}`}
                    onClick={() => setSelectedTour(t)}
                  >
                    <span className="tsc-icon">{t.icon}</span>
                    <div className="tsc-tag">{t.tag}</div>
                    <div className="tsc-name">{t.name}</div>
                    <div className="tsc-desc">{t.desc}</div>
                    <div className="tsc-meta">
                      <span>⏱ {t.duration}</span>
                      <span>📍 {t.location || "Nairobi"} </span>
                    </div>
                    <div className="tsc-price">${t.price} <span style={{ fontSize: ".65rem", color: "rgba(255,255,255,0.35)", fontFamily: "'Jost',sans-serif", fontWeight: 400 }}>/ person</span></div>
                  </div>
                ))}
              </div>

              <div className="btn-row">
                <button
                  className="btn-gold"
                  disabled={!selectedTour}
                  onClick={() => setStep(2)}
                >
                  Continue →
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 2: DETAILS ── */}
          {step === 2 && selectedTour && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: "2rem", alignItems: "start" }}>
              <div>
                <p style={{ fontSize: ".65rem", letterSpacing: "5px", textTransform: "uppercase", color: "var(--gold)", marginBottom: ".8rem" }}>
                  Step 2 of 3
                </p>
                <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 700, color: "#fff", marginBottom: "2rem" }}>
                  Tour Details
                </h2>

                <div className="sc-form-card">
                  {/* Guest count */}
                  <div className="sc-form-group">
                    <label className="sc-label">Number of Guests</label>
                    <div className="guest-counter">
                      <button className="gc-btn" onClick={() => setGuests(Math.max(1, guests - 1))}>−</button>
                      <span className="gc-num">{guests}</span>
                      <button className="gc-btn" onClick={() => setGuests(Math.min(50, guests + 1))}>+</button>
                      <span style={{ fontSize: ".8rem", color: "rgba(255,255,255,0.35)", marginLeft: ".5rem" }}>
                        {guests === 1 ? "person" : "people"}
                      </span>
                    </div>
                    {errors.guests && <div className="sc-error">{errors.guests}</div>}
                  </div>

                  {/* Date */}
                  <div className="sc-form-group">
                    <label className="sc-label">Tour Date</label>
                    <input
                      type="date"
                      className={`sc-input ${errors.tourDate ? "sc-input-error" : ""}`}
                      min={today}
                      value={tourDate}
                      onChange={(e) => setTourDate(e.target.value)}
                      style={{ colorScheme: "dark" }}
                    />
                    {errors.tourDate && <div className="sc-error">{errors.tourDate}</div>}
                  </div>

                  {/* Time */}
                  <div className="sc-form-group">
                    <label className="sc-label">Preferred Start Time</label>
                    <select
                      className={`sc-input ${errors.tourTime ? "sc-input-error" : ""}`}
                      value={tourTime}
                      onChange={(e) => setTourTime(e.target.value)}
                    >
                      {["08:00","08:30","09:00","09:30","10:00","10:30","11:00","14:00","14:30","15:00"].map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    {errors.tourTime && <div className="sc-error">{errors.tourTime}</div>}
                  </div>
                </div>

                <div className="btn-row">
                  <button className="btn-outline" onClick={() => setStep(1)}>← Back</button>
                  <button
                    className="btn-gold"
                    onClick={() => { if (validateStep2()) setStep(3); }}
                  >
                    Review Booking →
                  </button>
                </div>
              </div>

              {/* Sidebar summary */}
              <div className="summary-card" style={{ position: "sticky", top: "100px" }}>
                <p style={{ fontSize: ".6rem", letterSpacing: "3px", textTransform: "uppercase", color: "var(--gold)", marginBottom: "1rem" }}>Your Selection</p>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.3rem", fontWeight: 700, color: "#fff", marginBottom: "1rem" }}>{selectedTour.name}</div>
                <div className="summary-row"><span>Duration</span><strong>{selectedTour.duration}</strong></div>
                <div className="summary-row"><span>Price per person</span><strong>${selectedTour.price}</strong></div>
                <div className="summary-row"><span>Guests</span><strong>{guests}</strong></div>
                <div className="summary-total">
                  <div className="summary-row"><span>Total</span><strong>${total}</strong></div>
                </div>
                <p style={{ fontSize: ".72rem", color: "rgba(255,255,255,0.3)", marginTop: "1rem", lineHeight: "1.6" }}>
                  💵 Payment collected at the start of the tour.
                </p>
              </div>
            </div>
          )}

          {/* ── STEP 3: CONFIRM ── */}
          {step === 3 && selectedTour && (
            <div style={{ maxWidth: "640px", margin: "0 auto" }}>
              <p style={{ fontSize: ".65rem", letterSpacing: "5px", textTransform: "uppercase", color: "var(--gold)", marginBottom: ".8rem" }}>
                Step 3 of 3
              </p>
              <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 700, color: "#fff", marginBottom: "2rem" }}>
                Confirm & Book
              </h2>

              {/* Summary */}
              <div className="summary-card" style={{ marginBottom: "1.5rem" }}>
                <p style={{ fontSize: ".6rem", letterSpacing: "3px", textTransform: "uppercase", color: "var(--gold)", marginBottom: "1rem" }}>Booking Summary</p>
                <div className="summary-row"><span>Guest name</span><strong>{user?.firstName || "—"}</strong></div>
                <div className="summary-row"><span>Tour</span><strong>{selectedTour.name}</strong></div>
                <div className="summary-row"><span>Date</span><strong>{tourDate}</strong></div>
                <div className="summary-row"><span>Time</span><strong>{tourTime}</strong></div>
                <div className="summary-row"><span>Guests</span><strong>{guests}</strong></div>
                <div className="summary-row"><span>Duration</span><strong>{selectedTour.duration}</strong></div>
                <div className="summary-total">
                  <div className="summary-row"><span>Total Due On-Site</span><strong>${total}</strong></div>
                </div>
              </div>

              {/* WhatsApp note */}
              <div className="wa-note">
                <span className="wa-note-icon">💬</span>
                <p>
                  Clicking <strong>"Book via WhatsApp"</strong> will open a pre-filled WhatsApp message to our team. We'll confirm your booking within 2 hours. <strong>Payment is done at the start of the tour.</strong>
                </p>
              </div>

              <div className="btn-row">
                <button className="btn-outline" onClick={() => setStep(2)}>← Back</button>
                <button className="btn-whatsapp" onClick={handleBook}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  Book via WhatsApp
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
}

// Fallback inline styles for success screen
const styles = {
  root: { fontFamily: "'Jost',sans-serif", background: "#0A0A0A", color: "#fff", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem" },
  successWrap: { textAlign: "center", maxWidth: "480px" },
  successIcon: { width: "80px", height: "80px", borderRadius: "50%", background: "rgba(201,168,76,0.15)", border: "2px solid #C9A84C", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2rem", color: "#C9A84C", margin: "0 auto 2rem" },
  successTitle: { fontFamily: "'Cormorant Garamond',serif", fontSize: "2.5rem", fontWeight: 700, color: "#fff", marginBottom: "1rem" },
  successText: { fontSize: ".9rem", color: "rgba(255,255,255,0.6)", lineHeight: "1.8", marginBottom: "2rem" },
  btnGold: { background: "#C9A84C", color: "#0A0A0A", border: "none", padding: ".85rem 2.2rem", borderRadius: "4px", fontFamily: "'Jost',sans-serif", fontWeight: 600, fontSize: ".82rem", letterSpacing: "2px", textTransform: "uppercase", cursor: "pointer" },
  btnOutline: { background: "transparent", color: "#C9A84C", border: "1px solid #C9A84C", padding: ".85rem 2.2rem", borderRadius: "4px", fontFamily: "'Jost',sans-serif", fontWeight: 600, fontSize: ".82rem", letterSpacing: "2px", textTransform: "uppercase", cursor: "pointer" },
};
